import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@app/hooks";
import { selectWell } from "@app/slices/wellContextSlice";
import { useLocale } from "@hooks/useLocale";
import { useGetWellsQuery } from "@api/mApi";

/**
 * Well / pad switcher — top of the rail, under the brand. Expanded: a button
 * showing the current well. Collapsed: a compact avatar. The popover lists the
 * register (grouped basins are collapsed to a flat searchable list here);
 * picking one drives every screen through plan context and jumps to the finder.
 */
export function WellSwitcher({ collapsed }: { collapsed: boolean }) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useLocale();
  const selectedWellId = useAppSelector((s) => s.wellContext.selectedWellId);
  const { data: wells = [] } = useGetWellsQuery();

  const current = wells.find((w) => w.id === selectedWellId) ?? null;
  const currentName = current?.commonName ?? t("well.switcher.all");
  const initial = (current?.commonName ?? "•").trim().charAt(0).toUpperCase() || "•";

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? wells.filter(
        (w) => w.commonName.toLowerCase().includes(q) || w.uwi.toLowerCase().includes(q),
      )
    : wells;

  return (
    <div className="wellsw-wrap" ref={rootRef}>
      <button
        type="button"
        className="wellsw"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={t("topbar.wellSwitcher")}
        title={collapsed ? `${t("topbar.wellSwitcher")}: ${currentName}` : undefined}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="av" aria-hidden="true">
          {initial}
        </span>
        {!collapsed && (
          <>
            <span className="nm">
              <b>{currentName}</b>
              <small>{current ? current.uwi : t("finder.title")}</small>
            </span>
            <span className="chev" aria-hidden="true">
              ▾
            </span>
          </>
        )}
      </button>

      {open && (
        <div className="wellsw-pop open" role="listbox" id={menuId} aria-label={t("well.switcher.title")}>
          <label className="wpop-search">
            <span className="ic ic-search" aria-hidden="true" />
            <input
              type="search"
              placeholder={t("finder.filter.wellPlaceholder")}
              aria-label={t("finder.filter.well")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <a
            href="#all"
            className={!current ? "active" : ""}
            onClick={(e) => {
              e.preventDefault();
              dispatch(selectWell(null));
              setOpen(false);
              navigate("/finder");
            }}
          >
            {t("well.switcher.all")}
          </a>
          {filtered.map((w) => (
            <a
              key={w.id}
              href={`#${w.id}`}
              className={w.id === current?.id ? "active" : ""}
              onClick={(e) => {
                e.preventDefault();
                dispatch(selectWell(w.id));
                setOpen(false);
                navigate("/finder");
              }}
            >
              <span className="dot" aria-hidden="true" />
              {w.commonName}
              <span className="code" style={{ marginLeft: "auto", opacity: 0.7 }}>
                {w.uwi}
              </span>
            </a>
          ))}
          {filtered.length === 0 && <div className="dim" style={{ padding: 10 }}>{t("common.na")}</div>}
        </div>
      )}
    </div>
  );
}
